import Button from 'common/components/button/Button';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import PageHeader from 'common/components/pageHeader/PageHeader';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import Loading from 'common/services/Loading';
import { LOGGER_LOG_TYPE } from 'Config';
import Logger from 'common/services/Logger';
import Toast from 'common/services/Toast';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { NumerationDto } from 'api/numerations/models/NumerationDto';
import { NumerationCounterDto } from 'api/numerations/models/NumerationCounterDto';
import { NumerationType } from 'api/numerations/enums/NumerationType';
import NumerationsService from 'api/numerations/NumerationsService';
import { NumerationSchemeType } from 'api/numerations/enums/NumerationSchemeType';
import { NumerationIncrementType } from 'api/numerations/enums/NumerationIncrementType';
import { NumerationResetType } from 'api/numerations/enums/NumerationResetType';
import NumerationSchemeParser, { SchemeToken } from 'api/numerations/NumerationSchemeParser';
import CheckInput from 'common/components/checkInput/CheckInput';
import SelectInput from 'common/components/selectInput/SelectInput';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { Header, Label, SubHeader, Text } from 'common/components/texts/Texts';
import TextInput from 'common/components/textInput/TextInput';
import MoneyInput from 'common/components/moneyInput/MoneyInput';
import InputError from 'common/components/inputError/InputError';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from 'common/components/strictModeDroppable/StrictModeDroppable';
import Utils from 'common/services/Utils';
import { IoMoveOutline } from 'react-icons/io5';
import styles from './NumerationScreen.module.scss';
import WorkTypesService from 'api/workTypes/WorkTypesService';

export function reorder(list: SchemeToken[], startIndex: number, endIndex: number) {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
};

const MAX_RESULT_LENGTH = 100;

function getInitials(name: string): string {
    const words = name.trim().split(' ');
    const initials = words.map(word => word.charAt(0).toUpperCase()).join('');
    return initials;
}

const padZero = function (s: string, len: number) {
    const c = '0';
    while (s.length < len) s = c + s;
    return s;
}

// Bijective base-26: 1 -> A, 26 -> Z, 27 -> AA, 28 -> AB ...
function toAlpha(n: number): string {
    let s = '';
    while (n > 0) {
        n--;
        s = String.fromCharCode(65 + (n % 26)) + s;
        n = Math.floor(n / 26);
    }
    return s;
}

const geSchemeTokenExample = (token: SchemeToken, numberSize: number, userName: string, workTypeAbbreviation: string) => {
    switch (token.type) {
        case NumerationSchemeType.WORK_TYPE:
            return workTypeAbbreviation;
        case NumerationSchemeType.BUDGET_TYPE:
            return token.value ?? '';
        case NumerationSchemeType.USER_INITIALS:
            return getInitials(userName);
        case NumerationSchemeType.SEQUENTIAL_NUMBER:
            return padZero('1', numberSize);
        case NumerationSchemeType.SEQUENTIAL_ALPHABET:
            return toAlpha(1);
        case NumerationSchemeType.VERSION_ALPHABET:
            return toAlpha(1);
        case NumerationSchemeType.ORIGIN_NUMBER:
            return '00208';
        case NumerationSchemeType.CURRENT_MONTH:
            return padZero((new Date().getMonth() + 1).toString(), 2);
        case NumerationSchemeType.CURRENT_YEAR:
            return new Date().getFullYear();
        case NumerationSchemeType.CURRENT_YEAR_SMALL:
            return new Date().getFullYear().toString().substring(2);
        case NumerationSchemeType.TEXT:
            return token.value ?? '';
        default:
            return '';
    }
}

const RULE_DELIMITER = ',';

function parseRules(csv?: string): string[] {
    return (csv ?? '').split(RULE_DELIMITER).map(x => x.trim()).filter(Boolean);
}

function toggleRule(csv: string | undefined, value: string, checked: boolean): string {
    const set = new Set(parseRules(csv));
    if (checked) {
        set.add(value);
    } else {
        set.delete(value);
    }
    return Array.from(set).join(RULE_DELIMITER);
}

function NumerationScreen(): JSX.Element | null {
    const { id, type } = useParams<{ id: string, type: string }>();
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isDetails = type === 'details';
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['NUMERATIONS_WRITE']);
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['NUMERATIONS_READ']);
    // Hidden from the picker to avoid confusing the user; the enum stays so existing schemes still render.
    const hiddenSchemeTypes: string[] = [NumerationSchemeType.SEQUENTIAL_ALPHABET, NumerationSchemeType.BUDGET_TYPE];
    const schemeTypesOptions = useMemo(() => Object.keys(NumerationSchemeType)
        .filter(key => !hiddenSchemeTypes.includes(key))
        .map(key => ({
            label: t('numerations.scheme_types.' + key),
            value: key,
        })), [i18n.language]);
    const [model, setModel] = useState<NumerationDto>();
    const [schemeTokens, setSchemeTokens] = useState<SchemeToken[]>([]);
    const [counters, setCounters] = useState<NumerationCounterDto[]>([]);
    const isBudgets = model?.numerationType === NumerationType.BUDGETS;
    // When "increment separately by family" is OFF, all families share a single counter -> single input.
    const incrementByFamily = parseRules(model?.incrementSeparatelyBy).includes(NumerationIncrementType.WORK_TYPE);
    const sharedNextNumber = counters.length > 0 ? Math.max(...counters.map(c => c.currentNumber)) : 1;
    const sharedMinNumber = counters.length > 0 ? Math.max(...counters.map(c => c.minNumber)) : 1;
    // Resetting the sequence by year only makes sense if a year is part of the reference.
    const hasYearToken = schemeTokens.some(x => x.type === NumerationSchemeType.CURRENT_YEAR || x.type === NumerationSchemeType.CURRENT_YEAR_SMALL);
    const userName = loggedUser!.realName
    const [workTypeAbbreviation, setWorkTypeAbbreviation] = useState<string>('')
    const finalResultExample = schemeTokens.map((t) => geSchemeTokenExample(t, model?.numberSize ?? 0, userName, workTypeAbbreviation)).join('') ?? '';
    const [submitted, setSubmitted] = useState(false);

    const url = '/backoffice/numerations';
    const currentUrl = url + '/' + type + '/' + id;

    useEffect(() => {
        void getData()
    }, [id, type]);

    const getData = async () => {
        if (!id) {
            return;
        }

        try {
            Loading.show();
            const result = await NumerationsService.getById(id);
            const workTypes = await WorkTypesService.getList({ page: 1, itemsPerPage: 50 });

            setModel(result);
            setSchemeTokens(NumerationSchemeParser.parse(result.scheme));
            // Example uses the abbreviation that matches this numeration: TS sigla for supplementary works,
            // budget sigla otherwise. Pick the first family that actually has it filled.
            const isTs = result.numerationType === NumerationType.TS;
            const sampleAbbreviation = workTypes.items
                .map(x => isTs ? x.tsAbbreviation : x.budgetAbbreviation)
                .find(x => !!x);
            setWorkTypeAbbreviation(sampleAbbreviation || 'AB');

            if (result.numerationType === NumerationType.BUDGETS) {
                setCounters(await NumerationsService.getCounters(result.id));
            }

            Loading.hide();
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get numeration', error);
            Toast.error(t('messages.error_load_info'));
        }
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/backoffice/numerations/${typeUrl}/${id}`);
        } else {
            navigate('/backoffice/numerations');
        }
    }

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!model) {
            return;
        }

        setSubmitted(true);

        if (schemeTokens.find(x => !x.type)) {
            Toast.warning(t('messages.required_fields_empty'));
            return;
        }

        if (finalResultExample.length > MAX_RESULT_LENGTH) {
            Toast.warning(t('numerations.numeration.too_big_message'));
            return;
        }

        // The next number can't be set below the highest active budget already issued for that family.
        // Block locally (don't even hit the server) and tell the user which family is the problem.
        if (model.numerationType === NumerationType.BUDGETS) {
            if (incrementByFamily) {
                const invalid = counters.find(c => c.currentNumber < c.minNumber);
                if (invalid) {
                    Toast.warning(t('numerations.numeration.min_number_message', { family: invalid.workTypeName, min: invalid.minNumber }));
                    return;
                }
            } else if (sharedNextNumber < sharedMinNumber) {
                Toast.warning(t('numerations.numeration.min_number_message', { family: t('numerations.types.' + model.numerationType), min: sharedMinNumber }));
                return;
            }
        }

        try {
            Loading.show();

            // Year reset only applies when a year token exists; drop it otherwise to avoid hidden partitioning.
            await NumerationsService.update({
                ...model,
                resetBy: hasYearToken ? model.resetBy : '',
                scheme: NumerationSchemeParser.compose(schemeTokens),
            })

            if (model.numerationType === NumerationType.BUDGETS) {
                // When not incrementing per family, all families share one counter -> send a single value.
                const countersToSave = incrementByFamily
                    ? counters
                    : counters.map(c => ({ ...c, currentNumber: sharedNextNumber }));
                await NumerationsService.updateCounters(model.id, countersToSave);
                // Re-fetch so any server-side clamp (next number can't be below the highest active budget) is reflected.
                setCounters(await NumerationsService.getCounters(model.id));
            }

            Loading.hide();
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the numeration with id: ${id}`, error);
            Toast.error(t('messages.record_save_error'));
            Loading.hide();
        }
    };

    const onChangeSchemeToken = (value: Partial<SchemeToken>, key: string) => {
        setSchemeTokens(tokens => tokens.map((t) => {
            if (t.key === key) {
                return {
                    ...t,
                    ...value,
                }
            }
            return t;
        }));
    }

    const removeSchemeToken = (key: string) => {
        setSchemeTokens(tokens => tokens.filter((t) => t.key !== key));
    }

    const addSchemeToken = () => {
        setSchemeTokens(tokens => [...tokens, { type: null, value: undefined, key: Utils.newGuid() }]);
    }

    const onDragEnd = (result: any) => {
        if (!result.destination) {
            return;
        }

        const items = reorder(
            [...schemeTokens],
            result.source.index,
            result.destination.index
        );
        setSchemeTokens(items);
    }

    const getItemStyle = (isDragging: any, draggableStyle: any) => ({
        userSelect: 'none',
        ...draggableStyle
    });

    const entityName = model?.numerationType ? t('numerations.types.' + model.numerationType) : '';
    const titlePrefix = type === 'edit'
        ? t('common.edit_title.numeration')
        : t('common.details_title.numeration');
    const entityLabel = entityName || undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={title}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: t('numerations.list.title'), url },
                    { name: title, url: currentUrl },
                ]}
            />

            <PageHeader title={titlePrefix} subTitle={entityLabel} informationText={t('common.go_back')} onGoBack={() => navigateTo()}>
                {canWrite && !isDetails && <Button type='submit' form='form'>{t('common.save')}</Button>}
                {canWrite && isDetails && <Button fw href={`/backoffice/numerations/edit/${id}`}>{t('common.edit')}</Button>}
            </PageHeader>
            <PageContainer addBottomSpace>
                <form noValidate onSubmit={onSubmit} id="form">
                    <Header>{t('numerations.types.' + model?.numerationType)}</Header>
                    <br />
                    <Row>
                        {!isDetails && <Col xs='auto' style={{ width: 60 }}></Col>}
                        <Col>
                            <Label>{t('numerations.numeration.type')}</Label>
                        </Col>
                        <Col xs={2}>
                            <Label>{t('numerations.numeration.value')}</Label>
                        </Col>
                        <Col xs={2}>
                            <Label>{t('numerations.numeration.example')}</Label>
                        </Col>
                        {!isDetails && <Col xs={1}></Col>}
                    </Row>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <StrictModeDroppable droppableId="droppable" isDropDisabled={isDetails}>
                            {(provided, snapshot) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                >
                                    {schemeTokens.map((token, index) => {
                                        return (
                                            <Draggable key={token.key} draggableId={token.key} index={index} isDragDisabled={isDetails}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={getItemStyle(
                                                            snapshot.isDragging,
                                                            provided.draggableProps.style
                                                        )}
                                                    >
                                                        <Row className={`mb-2 ${styles.row}`}>
                                                            {!isDetails && (
                                                                <Col xs='auto' style={{ width: 60 }}>
                                                                    <span {...provided.dragHandleProps}>
                                                                        <IoMoveOutline />
                                                                    </span>
                                                                </Col>
                                                            )}
                                                            <Col>
                                                                <SelectInput
                                                                    options={schemeTypesOptions}
                                                                    value={token.type}
                                                                    onChange={v => onChangeSchemeToken({ type: v as NumerationSchemeType, value: '' }, token.key)}
                                                                    disabled={isDetails}
                                                                    hasError={!token.type && submitted}
                                                                />
                                                                {!token.type && submitted && <InputError error={{ type: 'required' }} />}
                                                            </Col>
                                                            <Col xs={2}>
                                                                <TextInput
                                                                    value={token.value ?? ''}
                                                                    onChange={e => onChangeSchemeToken({ value: e.target.value }, token.key)}
                                                                    disabled={(token.type !== NumerationSchemeType.TEXT && token.type !== NumerationSchemeType.BUDGET_TYPE) || isDetails}
                                                                />
                                                            </Col>
                                                            <Col xs={2} className={styles.alignCenter}>
                                                                <Text>{geSchemeTokenExample(token, model?.numberSize ?? 0, userName, workTypeAbbreviation)}</Text>
                                                            </Col>
                                                            {!isDetails && (
                                                                <Col xs={1}>
                                                                    <Button onClick={() => removeSchemeToken(token.key)}><FaTrash /></Button>
                                                                </Col>
                                                            )}
                                                        </Row>
                                                    </div>
                                                )}
                                            </Draggable>
                                        );
                                    })}
                                    {provided.placeholder}
                                </div>
                            )}
                        </StrictModeDroppable>
                    </DragDropContext>
                    {!isDetails && (
                        <Row className={styles.row}>
                            <Col></Col>
                            <Col xs={1}>
                                <Button onClick={addSchemeToken}><FaPlus /></Button>
                            </Col>
                        </Row>
                    )}

                    <br />

                    <div className='mb-3'>
                        <SubHeader space>{t('numerations.numeration.options')}</SubHeader>

                        <Row>
                            {schemeTokens.find(x => x.type === NumerationSchemeType.SEQUENTIAL_NUMBER) && (
                                <Col xs={12} md={4}>
                                    <Label space>{t('numerations.numeration.number_size')}</Label>
                                    <MoneyInput
                                        value={model?.numberSize}
                                        onChange={v => setModel(m => ({ ...m!, numberSize: v ?? 1 }))}
                                        hideUnitsDrop
                                        decimalScale={0}
                                        isAllowed={(values) => {
                                            const { floatValue } = values;
                                            return (floatValue ?? 0) >= 1 && (floatValue ?? 0) <= 20;
                                        }}
                                        disabled={isDetails}
                                    />
                                </Col>
                            )}
                        </Row>

                        <Row className='mt-3'>
                            <Col xs={12} md={6}>
                                <Label space>{t('numerations.numeration.increment_separately_by')}</Label>
                                <Row>
                                    {Object.values(NumerationIncrementType).map(key => (
                                        <Col xs={12} md={6} key={key}>
                                            <CheckInput
                                                checked={parseRules(model?.incrementSeparatelyBy).includes(key)}
                                                onChange={e => setModel(m => ({ ...m!, incrementSeparatelyBy: toggleRule(m?.incrementSeparatelyBy, key, e.target.checked) }))}
                                                disabled={isDetails}
                                            >
                                                {t('numerations.numeration.increment_types.' + key)}
                                            </CheckInput>
                                        </Col>
                                    ))}
                                </Row>
                            </Col>
                            {hasYearToken && (
                                <Col xs={12} md={6}>
                                    <Label space>{t('numerations.numeration.reset_by')}</Label>
                                    <Row>
                                        {[NumerationResetType.YEAR].map(key => (
                                            <Col xs={12} md={6} key={key}>
                                                <CheckInput
                                                    checked={parseRules(model?.resetBy).includes(key)}
                                                    onChange={e => setModel(m => ({ ...m!, resetBy: toggleRule(m?.resetBy, key, e.target.checked) }))}
                                                    disabled={isDetails}
                                                >
                                                    {t('numerations.numeration.reset_types.' + key)}
                                                </CheckInput>
                                            </Col>
                                        ))}
                                    </Row>
                                </Col>
                            )}
                        </Row>

                        {isBudgets && incrementByFamily && (
                            <div className='mt-4'>
                                <Label space>{t('numerations.numeration.next_number_by_family')}</Label>
                                {counters.map(c => (
                                    <Row key={c.workTypeId} className='mb-2'>
                                        <Col xs={6} md={4} className={styles.alignCenter}>
                                            <Text>{c.workTypeName}</Text>
                                            {c.minNumber > 1 && (
                                                <OverlayTrigger placement="top" overlay={<Tooltip>{t('numerations.numeration.min_number_tooltip', { min: c.minNumber })}</Tooltip>}>
                                                    <span><InfoIcon style={{ color: '#000' }} /></span>
                                                </OverlayTrigger>
                                            )}
                                        </Col>
                                        <Col xs={6} md={4}>
                                            <MoneyInput
                                                value={c.currentNumber}
                                                onChange={v => setCounters(cs => cs.map(x => x.workTypeId === c.workTypeId ? { ...x, currentNumber: v ?? 1 } : x))}
                                                hideUnitsDrop
                                                decimalScale={0}
                                                isAllowed={(values) => (values.floatValue ?? 0) >= 1}
                                                disabled={isDetails}
                                            />
                                        </Col>
                                    </Row>
                                ))}
                            </div>
                        )}

                        {isBudgets && !incrementByFamily && (
                            <div className='mt-4'>
                                <Label space>{t('numerations.numeration.next_number')}</Label>
                                <Row className='mb-2'>
                                    <Col xs={6} md={4} className={styles.alignCenter}>
                                        <Text>{t('numerations.types.' + model?.numerationType)}</Text>
                                        {sharedMinNumber > 1 && (
                                            <OverlayTrigger placement="top" overlay={<Tooltip>{t('numerations.numeration.min_number_tooltip', { min: sharedMinNumber })}</Tooltip>}>
                                                <span><InfoIcon style={{ color: '#000' }} /></span>
                                            </OverlayTrigger>
                                        )}
                                    </Col>
                                    <Col xs={6} md={4}>
                                        <MoneyInput
                                            value={sharedNextNumber}
                                            onChange={v => setCounters(cs => cs.map(x => ({ ...x, currentNumber: v ?? 1 })))}
                                            hideUnitsDrop
                                            decimalScale={0}
                                            isAllowed={(values) => (values.floatValue ?? 0) >= 1}
                                            disabled={isDetails}
                                        />
                                    </Col>
                                </Row>
                            </div>
                        )}
                    </div>

                    <div className='mt-5'>
                        <SubHeader>{t('numerations.numeration.final_result_example')}:</SubHeader>
                    </div>
                    <Text>{finalResultExample}</Text>
                    {finalResultExample.length > MAX_RESULT_LENGTH && <InputError error={{ type: 'value', message: t('numerations.numeration.too_big_message') }} isWarning />}
                </form>
            </PageContainer>
        </ScreenTitle>
    )
}

export default NumerationScreen;
